<!--
 * @Author: Tahiti
 * @Date: 2025-10-20 13:34:19
 * @LastEditors: Tahiti
 * @LastEditTime: 2026-01-06 22:58:20
 * @Description: 官网首页（根据登录状态动态切换 Layout）
 * @FilePath: /weryai-web/pages/index.vue
-->
<template>
    <NuxtLayout :name="currentLayout">
        <div :class="containerClass">
            <!-- SEO H1 标签 - 视觉隐藏但对搜索引擎和屏幕阅读器可见 -->
            <h1 class="sr-only">{{ t('seo.home.h1') }}</h1>

            <!-- 顶部 Hero 区域：PC端一行展示，移动端纵向排列 -->
            <div class="hero-section relative z-[1] flex flex-col gap-[10px] lg:flex-row lg:h-[220px]">
                <!-- 第一列：图片生成卡片（移动端隐藏，用快捷入口替代） -->
                <div class="hidden lg:block lg:flex-1 min-w-0 h-[220px]">
                    <!-- LCP 关键卡片：不要 Lazy，避免异步组件导致 element render delay -->
                    <!--
                        关键：首屏 LCP 图必须 SSR 直接输出 <img>，不能因为 bannerList 暂时为空而走 skeleton，
                        否则浏览器会延后发起图片请求（Lighthouse 里表现为 resource load delay / element render delay）。
                        这里通过 bannerList 的“兜底项”保证永远有内容，因此不再使用 v-if/skeleton。
                    -->
                    <WebsiteBannerCard :item="bannerList[0]" type="image" :priority="true" />
                </div>

                <!-- 第二列：视频生成卡片，高度 220px（移动端隐藏） -->
                <div class="hidden lg:block lg:flex-1 min-w-0 h-[220px]">
                    <WebsiteBannerCard :item="bannerList[1]" type="video" />
                </div>

                <!-- 第三列：音乐生成 + 角色 纵向堆叠，固定宽度 268px，单个 104px（移动端隐藏） -->
                <div class="hidden lg:flex flex-col gap-[12px] lg:w-[268px] lg:flex-shrink-0">
                    <div class="h-[104px]">
                        <WebsiteBannerCard v-if="bannerList[2]" :item="bannerList[2]" type="music" />
                    </div>
                    <div class="h-[104px]">
                        <WebsiteBannerCard v-if="bannerList[3]" :item="bannerList[3]" type="character" />
                    </div>
                </div>

            </div>

            <!-- 移动端快捷入口 -->
            <div class="mt-4 lg:hidden">
                <MobileQuickEntry />
            </div>

            <!-- AI 模型 -->
            <div class="mt-5">
                <ClientOnly>
                    <Suspense>
                        <template #default>
                            <LazyWebsiteAIModels />
                        </template>
                        <template #fallback>
                            <!--
                                重要：骨架高度必须与真实 `WebsiteAIModels` 保持一致，避免切换时高度缩放造成 CLS。
                                真实组件的标题是 sr-only（不占可视高度），文案在卡片内遮罩层里（不额外增加卡片外高度）。
                                因此这里骨架只做“卡片本体”占位，不再额外渲染标题/卡片下方两行占位。
                            -->
                            <div class="flex gap-5 overflow-hidden" aria-hidden="true">
                                <div v-for="i in 5" :key="i" class="flex-shrink-0">
                                    <div
                                        class="w-[140px] h-[190px] lg:w-[220px] lg:h-[300px] rounded-[5px] lg:rounded-[12px] bg-[#1b1d25] animate-pulse">
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Suspense>
                    <template #fallback>
                        <div class="flex gap-5 overflow-hidden" aria-hidden="true">
                            <div v-for="i in 5" :key="i" class="flex-shrink-0">
                                <div
                                    class="w-[140px] h-[190px] lg:w-[220px] lg:h-[300px] rounded-[5px] lg:rounded-[12px] bg-[#1b1d25] animate-pulse">
                                </div>
                            </div>
                        </div>
                    </template>
                </ClientOnly>
            </div>



            <!-- AI 工具 -->
            <MountWhenVisible class="mt-10" :minHeight="480">
                <ClientOnly>
                    <Suspense>
                        <template #default>
                            <LazyWebsiteAITools />
                        </template>
                        <template #fallback>
                            <div>
                                <div class="h-[15px] lg:h-8 w-24 bg-[#1b1d25] rounded mb-5"></div>
                                <div class="flex gap-5 overflow-hidden">
                                    <div v-for="i in 4" :key="i" class="flex-shrink-0">
                                        <div
                                            class="w-[140px] h-[190px] lg:w-[300px] lg:h-[200px] rounded-xl bg-[#1b1d25] animate-pulse">
                                        </div>
                                        <div class="h-[15px] lg:h-5 w-32 bg-[#1b1d25] rounded mt-3"></div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Suspense>
                    <template #fallback>
                        <div>
                            <div class="h-[15px] lg:h-8 w-24 bg-[#1b1d25] rounded mb-5"></div>
                            <div class="flex gap-5 overflow-hidden">
                                <div v-for="i in 4" :key="i" class="flex-shrink-0">
                                    <div
                                        class="w-[140px] h-[190px] lg:w-[300px] lg:h-[200px] rounded-xl bg-[#1b1d25] animate-pulse">
                                    </div>
                                    <div class="h-[15px] lg:h-5 w-32 bg-[#1b1d25] rounded mt-3"></div>
                                </div>
                            </div>
                        </div>
                    </template>
                </ClientOnly>
            </MountWhenVisible>

            <!-- AI 特效（视频特效 + 图片特效） -->
            <MountWhenVisible class="mt-10" :minHeight="680">
                <ClientOnly>
                    <Suspense>
                        <template #default>
                            <LazyWebsiteEffects />
                        </template>
                        <template #fallback>
                            <div>
                                <div class="h-[15px] lg:h-8 w-32 bg-[#1b1d25] rounded mb-5"></div>
                                <div class="flex gap-5 overflow-hidden">
                                    <div v-for="i in 4" :key="i" class="flex-shrink-0">
                                        <div class="w-[250px] h-[300px] rounded-xl bg-[#1b1d25] animate-pulse"></div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </Suspense>
                    <template #fallback>
                        <div>
                            <div class="h-[15px] lg:h-8 w-32 bg-[#1b1d25] rounded mb-5"></div>
                            <div class="flex gap-5 overflow-hidden">
                                <div v-for="i in 4" :key="i" class="flex-shrink-0">
                                    <div class="w-[250px] h-[300px] rounded-xl bg-[#1b1d25] animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </template>
                </ClientOnly>
            </MountWhenVisible>

            <!--
                SEO 内容模块（影子状态判断：解决机器人抓取与 CLS 冲突）

                CLS 根因：
                - 使用 v-if 会导致该区块在 SSR/CSR 不一致时“整块插入/移除”，把 footer 往上/往下拉，CLS 很高
                解决方式：
                - 始终渲染一个固定占位容器（min-height），内部再按需渲染 SEO 组件
                - 这样即便发生极端登录态翻转，也不会出现大幅布局跳动
            -->
            <div class="min-h-[36px] seo-content-slot" :class="homeSeoRendered ? 'mt-4' : 'mt-0'">
                <WebsiteSeoContent v-if="homeSeoRendered" />
            </div>
        </div>
        <AppFooter />
    </NuxtLayout>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { definePageMeta, useHead, useState } from '#imports'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { useUserStore } from '~/stores/user'
import { useI18n } from '~/composables/useI18n'
import { useToolsHooks } from '~/utils/useHooks'

const { t } = useI18n()
const { bannerHeaderList } = useToolsHooks()

type HeroBannerItem = {
    title: string
    path: string
}

/**
 * 取前四个卡片数据（图片生成、视频生成、音乐生成、角色）
 *
 * LCP/性能关键点：
 * - 首屏必须"确定性渲染"LCP 大图（Generate Image 的背景图）
 * - 如果 bannerList 在 SSR/CSR 的某个阶段为空，模板就会退回 skeleton，
 *   造成浏览器延后发起图片请求（Lighthouse 里体现为 resource load delay / element render delay）
 *
 * 因此这里做兜底：即便 hooks 返回空，也保证 4 个入口永远有值。
 */
const bannerList = computed<
    [HeroBannerItem, HeroBannerItem, HeroBannerItem, HeroBannerItem]
>(() => {
    const list = (bannerHeaderList.value || []) as Array<HeroBannerItem>

    const fallback: [
        HeroBannerItem,
        HeroBannerItem,
        HeroBannerItem,
        HeroBannerItem,
    ] = [
            {
                title: String(t('homeView.image_create')),
                path: '/text-to-image',
            },
            {
                title: String(t('homeView.video_create')),
                path: '/image-to-video',
            },
            {
                title: String(t('homeView.music_create') || 'AI Music'),
                path: '/text-to-music',
            },
            { title: String(t('menus.videoEffects')), path: '/video-effects' },
        ]

    // 优先用 hooks（已做 formatPath），否则用 fallback（至少保证 SSR 输出稳定）
    return [
        (list[0] as HeroBannerItem) || fallback[0],
        (list[1] as HeroBannerItem) || fallback[1],
        (list[2] as HeroBannerItem) || fallback[2],
        (list[3] as HeroBannerItem) || fallback[3],
    ]
})

// 组件使用 Lazy* 形式，交给 Nuxt 自动做异步分包，降低首屏 JS 体积

const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const userInfo = computed(() => userStore.userInfo)

/**
 * 首页 SEO 内容是否渲染（冻结为 SSR 的一次性判定）
 *
 * 背景：部分老用户可能只在 localStorage 有 token，但 Cookie 没有。
 * 这种情况下 SSR 会认为“未登录”并渲染 SEO 内容；客户端恢复登录态后再移除，
 * 会把 footer 整块往上拉，造成严重 CLS。
 *
 * 这里用 useState 把“是否渲染”固定为服务端首屏的决定，避免 hydration 期间翻转。
 */
const homeSeoRendered = useState<boolean>('homeSeoRendered', () => {
    return !isLoggedIn.value && !userStore.hasToken
})

// 禁用默认 layout，使用动态 layout
definePageMeta({
    layout: false,
})

// 预加载 LCP 关键图片（BannerCard 背景图 - thumbnail 版本用于首屏快速显示）
useHead({
    link: [
        // 首屏最重要的图片 - Image 卡片背景（大卡片，LCP 元素）
        // 注意：这里必须与 `components/website/BannerCard.vue` 的 thumbnail URL 对齐
        {
            rel: 'preload',
            as: 'image',
            href: 'https://static.weryai.com/growth/image/weryai_2703957be9cd6427fa545c61922cddf2.png',
            fetchpriority: 'high',
            crossorigin: 'anonymous',
        },
        // 第二张图（小卡片：video）可能仍在首屏，但优先级低于 LCP，使用 prefetch 降低抢带宽风险
        {
            rel: 'prefetch',
            as: 'image',
            href: 'https://static.weryai.com/growth/image/weryai_f075d74b22ee1c1e4afa28f53baaf848.png',
            crossorigin: 'anonymous',
        },
        // Character 卡片背景（小卡片）：不再 preload，避免和 LCP 抢优先级（需要时再由浏览器正常加载）
    ],
})

// 固定使用 default 布局
const currentLayout = computed(() => {
    // 保持原有行为：固定使用 default 布局
    return 'default' as const
})

// 根据登录状态设置容器样式
const containerClass = computed(() => {
    // return isLoggedIn.value
    //     ? 'text-white w-full h-full pb-10' // 登录状态：类似原 home 页面
    //     : 'website-home mx-auto max-w-[1920px] px-4 md:px-8 lg:px-12 pt-20 pb-10' // 未登录状态：增加顶部间距和左右边距

    return 'text-white w-full h-full pt-5 pb-10'
})

// 使用多语言 SEO Meta 标签
useMultilingualSeo({ pageType: 'home' })

// 用户退出登录后：如果在首页，允许再次展示 SEO（不影响首屏 SSR 冻结）
watch(
    () => userStore.isLoggedIn,
    (val, oldVal) => {
        if (oldVal === true && val === false) {
            homeSeoRendered.value = true
        } else if (val) {
            homeSeoRendered.value = false
        }
    },
    {
        immediate: true,
    }
)

// 页面浏览埋点
onMounted(() => {
    analyticsEvent('page_view_web')
})
</script>

<style lang="less" scoped>
.website-home {
    min-height: calc(100vh - 52px);
    overflow-x: hidden;
    background-color: #000;
}

/* Screen reader only - 视觉隐藏但对 SEO 和屏幕阅读器可见 */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/**
 * SEO 内容占位容器：
 * - content-visibility: auto 让浏览器在离屏时跳过渲染，减少首屏开销
 * - contain-intrinsic-size 提供一个稳定的“预估尺寸”，进一步降低潜在 CLS 风险
 *
 * 注意：这里仍然保留 min-h-[180px] 作为基础占位，避免 SSR/CSR 翻转时出现插入式位移。
 */
.seo-content-slot {
    content-visibility: auto;
    contain-intrinsic-size: 180px;
}
</style>
